import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_ESTIMATED_DURATION_MS = 30000;
const COMPLETION_ANIMATION_MS = 150;

type UseProgressFacadeProps = {
	/**
	 * When true, the progress rapidly animates from its current value to 100%.
	 */
	isComplete?: boolean;
	/**
	 * Fires after the completion animation finishes (progress reaches 100%).
	 * Use this to trigger the transition to the next UI state.
	 */
	onCompleteAnimationFinished?: () => void;
	/**
	 * Estimated duration in milliseconds for the facade to reach its target.
	 * Defaults to 30000 (30 seconds).
	 */
	estimatedDurationMs?: number;
	/**
	 * Epoch ms (e.g. `Date.now()`) marking when the loading session started. When
	 * provided, elapsed time is measured from this shared anchor instead of the
	 * component's own mount time, so multiple bars driven by the same session stay
	 * in sync across mount/unmount. When omitted, progress anchors to mount.
	 */
	startedAt?: number | null;
	/**
	 * Fixed facade target (0-100) to animate towards. When provided, replaces the
	 * per-mount random target so multiple bars fill to the same value. When
	 * omitted, a random target (85-95%) is used.
	 */
	target?: number | null;
};

/**
 * Hook that produces a smooth facade progress value (0-100) for loading bars.
 *
 * - Smoothly animates from 0 → a random target (85-95%) over the estimated duration
 * - Hovers at the target until `isComplete` becomes true
 * - When `isComplete`, rapidly fills to 100% and calls `onCompleteAnimationFinished`
 */
export function useProgressFacade({
	isComplete = false,
	onCompleteAnimationFinished,
	estimatedDurationMs = DEFAULT_ESTIMATED_DURATION_MS,
	startedAt = null,
	target,
}: UseProgressFacadeProps): number {
	const randomTargetRef = useRef(85 + Math.random() * 10);
	const targetValue = target ?? randomTargetRef.current;

	// When anchored to a shared start time, seed the initial value from elapsed
	// time so a freshly-mounted bar jumps to the in-progress value instead of
	// restarting the animation from 0.
	const [progress, setProgress] = useState(() => {
		if (startedAt == null) {
			return 0;
		}
		const elapsed = Math.max(0, Date.now() - startedAt);
		return Math.min(1, elapsed / estimatedDurationMs) * targetValue;
	});

	const completionStartValueRef = useRef(0);
	const completionStartTimeRef = useRef<number | null>(null);
	const hasCalledFinishedRef = useRef(false);

	const onCompleteAnimationFinishedRef = useRef(onCompleteAnimationFinished);
	onCompleteAnimationFinishedRef.current = onCompleteAnimationFinished;

	// Phase 1: Animate from 0 → target over estimatedDurationMs.
	// When `startedAt` is provided, elapsed is measured from that shared anchor
	// (wall-clock) so every bar agrees regardless of mount time; otherwise it is
	// measured from the first animation frame of this mount.
	useEffect(() => {
		if (isComplete) {
			return;
		}

		let rafId: number;
		let mountStartTime: number | null = null;

		const tick = (timestamp: number) => {
			let elapsed: number;
			if (startedAt != null) {
				elapsed = Math.max(0, Date.now() - startedAt);
			} else {
				if (mountStartTime === null) {
					mountStartTime = timestamp;
				}
				elapsed = timestamp - mountStartTime;
			}
			const linearProgress = Math.min(1, elapsed / estimatedDurationMs);

			setProgress(linearProgress * targetValue);

			if (linearProgress < 1) {
				rafId = requestAnimationFrame(tick);
			}
		};

		rafId = requestAnimationFrame(tick);

		return () => {
			cancelAnimationFrame(rafId);
		};
	}, [isComplete, estimatedDurationMs, startedAt, targetValue]);

	// Phase 2: When isComplete, rapidly animate from current value → 100
	const handleCompletion = useCallback(() => {
		completionStartTimeRef.current = null;
		hasCalledFinishedRef.current = false;
		let rafId: number;

		const tick = (timestamp: number) => {
			if (completionStartTimeRef.current === null) {
				completionStartTimeRef.current = timestamp;
			}
			const elapsed = timestamp - completionStartTimeRef.current;
			const t = Math.min(1, elapsed / COMPLETION_ANIMATION_MS);
			const startValue = completionStartValueRef.current;
			const value = startValue + (100 - startValue) * t;

			setProgress(value);

			if (t < 1) {
				rafId = requestAnimationFrame(tick);
			} else if (!hasCalledFinishedRef.current) {
				hasCalledFinishedRef.current = true;
				onCompleteAnimationFinishedRef.current?.();
			}
		};

		rafId = requestAnimationFrame(tick);

		return () => {
			cancelAnimationFrame(rafId);
		};
	}, []);

	useEffect(() => {
		if (!isComplete) {
			return;
		}

		completionStartValueRef.current = progress;

		const cleanup = handleCompletion();
		return cleanup;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only trigger when isComplete changes to true
	}, [isComplete, handleCompletion]);

	return progress;
}
